/**
 * ProfileCompletionBanner - soft persistent nudge on /subjects when
 * the student is missing one or more "useful" profile fields after
 * onboarding. Replaces the legacy step 3 of onboarding (phone,
 * província, opt-in) with a non-blocking banner that points at
 * /profile?section=detalhes.
 *
 * Show conditions (parent decides):
 *   - student.phone_number is null/empty, OR
 *   - student.province is null/empty
 *
 * Dismissible - re-show window is admin-configurable (default 14 days)
 * via the Banners & Pop-ups admin tab. The student fixing the fields on
 * /profile makes the banner disappear naturally because the show
 * conditions stop matching.
 *
 * 2026-04-28 launch flow.
 */

import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const STORAGE_KEY = 'profile_banner_dismissed_until'

export interface ProfileCompletionBannerProps {
  /** Which fields are still missing - drives the message copy so the
   *  student knows exactly what they're being asked to fill. */
  missingFields: Array<'phone_number' | 'province'>
  dismissDays?: number
}

export const ProfileCompletionBanner: React.FC<ProfileCompletionBannerProps> = ({
  missingFields,
  dismissDays = 14,
}) => {
  const navigate = useNavigate()
  const [hidden, setHidden] = useState(false)

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now() + dismissDays * 86_400_000)) } catch {}
    setHidden(true)
  }

  if (hidden) return null
  if (missingFields.length === 0) return null

  // Build a friendly comma-list of missing fields. Two known fields
  // means "província e número"; one means just that one.
  const labels: Record<string, string> = {
    phone_number: 'número de telefone',
    province:     'província',
  }
  const niceList = missingFields.map(f => labels[f] ?? f)
  const phrase = niceList.length === 1
    ? niceList[0]
    : `${niceList.slice(0, -1).join(', ')} e ${niceList[niceList.length - 1]}`

  return (
    <div className="mb-5 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 flex items-start gap-3">
      <span className="text-2xl shrink-0 mt-0.5">📋</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-indigo-900 leading-snug">
          Faltam alguns dados ({phrase}) para o teu perfil ficar completo.{' '}
          <button
            onClick={() => navigate('/profile?section=detalhes')}
            className="font-semibold underline hover:text-indigo-700"
          >
            Completar agora →
          </button>
        </p>
      </div>
      <button
        onClick={dismiss}
        aria-label="Dispensar"
        className="text-indigo-400 hover:text-indigo-600 text-lg leading-none shrink-0"
      >
        ✕
      </button>
    </div>
  )
}

/** Compute which profile fields are still missing for a student. Used
 *  by SubjectSelectPage to decide whether to show the banner AND to
 *  feed the copy. */
export function missingProfileFields(user: {
  phone_number?: string | null
  province?:     string | null
}): Array<'phone_number' | 'province'> {
  const out: Array<'phone_number' | 'province'> = []
  if (!user.phone_number || !user.phone_number.trim()) out.push('phone_number')
  if (!user.province     || !user.province.trim())     out.push('province')
  return out
}

/** Has the student dismissed the banner within its re-show window? */
export function isProfileBannerDismissed(_dismissDays?: number): boolean {
  try {
    const until = Number(localStorage.getItem(STORAGE_KEY) ?? '0')
    return until > Date.now()
  } catch {
    return false
  }
}
